import { Button, Dialog, IconButton } from '@mui/material';
import FormFooter, { FormFooterProps } from 'components/shared/FormFooter';
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import ApplicationContent, { ApplicationContentSectionView } from 'store/types/ApplicationContent';
import { getFormattedSections, getReviewSectionId, getSectionId } from 'util/Application';
import { useWindowSize } from 'util/Window';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import classNames from 'classnames';

import styles from './ApplicationPageLayout.module.scss';

interface ApplicationPageLayoutProps {
  data?: ApplicationContent;
  footerProps?: PropsWithChildren<FormFooterProps>;
  isReviewForm?: boolean;
  isPrintActive?: boolean;
}

const ApplicationPageLayout: React.FunctionComponent<ApplicationPageLayoutProps> = ({
  data,
  footerProps,
  children,
  isReviewForm,
  isPrintActive,
}) => {
  const [sections, setSections] = useState<ApplicationContentSectionView[]>([]);
  const [collapsed, setCollapsed] = useState<boolean | null>(null);
  const [activeSectionIndex, setActiveSectionIndex] = useState<number>(0);
  const { isMobile } = useWindowSize();

  useEffect(() => {
    if (data) {
      setSections(getFormattedSections(data.sections));
    }
  }, [data]);

  useEffect(() => {
    if (isMobile && collapsed === null) {
      setCollapsed(true);
    }
  }, [collapsed, isMobile]);

  const handleMenuButtonClick = () => {
    setCollapsed(!collapsed);
  };

  useEffect(() => {
    if (!sections.length) return;

    const topMargin = isReviewForm ? '-90px' : '0px';
    let lastScrollTop = 0;

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const visibleEntries = entries.filter((e) => e.isIntersecting);

        if (visibleEntries.length > 0) {
          const scrollContainer = document.querySelector(`.${styles.fullPageContent}`);
          const currentScrollTop = scrollContainer?.scrollTop || 0;
          const scrollingDown = currentScrollTop > lastScrollTop;
          lastScrollTop = currentScrollTop;

          if (scrollingDown) {
            const lastEntry = visibleEntries[visibleEntries.length - 1];
            const index = lastEntry.target.getAttribute('data-section-index');
            if (index) setActiveSectionIndex(parseInt(index, 10));
          } else {
            const firstEntry = visibleEntries[0];
            const index = firstEntry.target.getAttribute('data-section-index');
            if (index) setActiveSectionIndex(parseInt(index, 10));
          }
        }
      },
      {
        root: null,
        rootMargin: `${topMargin} 0% -90% 0%`,
        threshold: 0,
      }
    );

    const scrollContainer = document.querySelector(`.${styles.fullPageContent}`);

    const handleScroll = () => {
      if (!scrollContainer) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setActiveSectionIndex(sections.length - 1);
      }
    };

    sections.forEach((_, index) => {
      const id = getSectionId(index);
      const element = document.getElementById(id);
      if (element) {
        element.setAttribute('data-section-index', index.toString());
        observer.observe(element);
      }
    });

    scrollContainer?.addEventListener('scroll', handleScroll);

    return () => {
      observer.disconnect();
      scrollContainer?.removeEventListener('scroll', handleScroll);
    };
  }, [sections, children, isReviewForm]);

  const handleLinkClick = useCallback(
    (index: number) => () => {
      const sectionElement: HTMLElement | null = document.getElementById(getSectionId(index));
      const reviewSectionElement: HTMLElement | null = document.getElementById(getReviewSectionId(index));

      if (sectionElement) {
        sectionElement.scrollIntoView();
      }
      if (reviewSectionElement) {
        reviewSectionElement.scrollIntoView();
      }
    },
    []
  );

  const headerOffset = isReviewForm ? '90px' : '0px';

  return (
    <>
      <Dialog
        open={true}
        fullScreen={true}
        className={classNames({ [styles.collapsed]: collapsed })}
        style={{ '--header-offset': headerOffset } as React.CSSProperties}
      >
        {!isPrintActive && (
          <aside className={styles.fullPageSidebar}>
            <ul>
              {sections.map((section, index) => {
                const isActive = activeSectionIndex === index;

                return (
                  <li
                    key={`${section.title}${index}-icon`}
                    className={classNames(styles.link, { [styles.activeLink]: isActive })}
                    onClick={handleLinkClick(index)}
                  >
                    <IconButton
                      classes={{ root: styles.linkIcon }}
                      color={isActive ? 'secondary' : 'primary'}
                      sx={isActive ? { backgroundColor: 'action.selected' } : {}}
                    >
                      {++index}
                    </IconButton>
                    <span className={styles.linkLabel}>{section.title}</span>
                  </li>
                );
              })}
            </ul>
            <Button variant={'text'} onClick={handleMenuButtonClick} className={styles.menuButton}>
              {collapsed ? <KeyboardDoubleArrowRightIcon /> : <KeyboardDoubleArrowLeftIcon />}
            </Button>
          </aside>
        )}
        <div className={classNames({ [styles.fullPageContent]: !isPrintActive })}>{children}</div>
        {!isPrintActive && <FormFooter {...footerProps} className={styles.fullPageFormFooter} />}
      </Dialog>
    </>
  );
};
export default ApplicationPageLayout;
